<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { Feedback } from '@oat-sa-private/ui-elements';
    import choiceFeedbackFactory from './choiceFeedbackHelper.js';
    import { getLanguageDirection } from '@oat-sa-private/ui-core';
    import { untrack, onDestroy } from 'svelte';
    import { wait } from '../../util/async.js';

    let {
        selectedNumber, // number of currently selected choices
        isInteractionFocused, // true when key focus moved to interaction
        type, // 'choices' or 'associations' or 'selectChoices' or 'placeAnswers'
        maxChoices,
        minChoices,
        qtiMaxChoicesMessage = null,
        qtiMinChoicesMessage = null,
        interactionElement, // bind to interaction element
        lang,
        immediateValidationWarning = $bindable(false),
        immediateValidationMessage = $bindable(null)
    } = $props();

    // instantiate feedback helper
    // svelte-ignore state_referenced_locally
    const setConstraintsFeedback = choiceFeedbackFactory({
        type,
        maxChoices,
        minChoices,
        qtiMaxChoicesMessage,
        qtiMinChoicesMessage
    });

    /**
     * "true" allow changing feedback status. It is possible in two cases:
     * 1 - key focus moved out of interaction
     * 2 - on selecting choice
     * TODO: 3 - when returning to an invalid interacted item
     */
    let updateFeedbackStatus = $state(false);
    let timeoutId;
    const immediateValidationWarningDuration = 2000;

    $effect(() => {
        if (isInteractionFocused && (selectedNumber || selectedNumber === 0)) {
            untrack(async () => {
                // if interaction was invalid and wasn't focused, then user clicked on a choice and interaction became valid,
                //   then feedback will flicker (time diff between focusin and selectedNumber change) --> delay updating status
                await wait(100);
                updateFeedbackStatus = true;
            });
        }
    });

    let constraintsFeedback = $derived(setConstraintsFeedback(selectedNumber, updateFeedbackStatus));
    let message = $derived(immediateValidationMessage || constraintsFeedback.message);
    let status = $derived(immediateValidationWarning ? 'warning' : constraintsFeedback.status);

    let dir = $derived(lang ? getLanguageDirection(lang) : void 0);

    $effect(() => {
        if (immediateValidationWarning) {
            untrack(() => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                timeoutId = setTimeout(() => {
                    immediateValidationWarning = false;
                    immediateValidationMessage = null;
                    timeoutId = null;
                }, immediateValidationWarningDuration);
            });
        } else {
            untrack(() => {
                clearTimeout(timeoutId);
                timeoutId = null;
            });
        }
    });

    /**
     * Trigger updating feedback status in case of moving focus out of interaction by switching tab or window
     */
    function handleBlurring() {
        if (isInteractionFocused) {
            updateFeedbackStatus = true;
        }
    }

    /**
     * Trigger updating feedback status in case of moving focus out of interaction by pressing tab
     * @param {Event} e
     */
    function handleFocusIn(e) {
        const closestInteractionParentElement = e.target.closest('.qti-interaction');
        // check that we out of interaction
        if (isInteractionFocused && !updateFeedbackStatus && !closestInteractionParentElement) {
            updateFeedbackStatus = true;

            // check that focus moved to another interaction
        } else if (
            isInteractionFocused &&
            !updateFeedbackStatus &&
            !interactionElement.isSameNode(closestInteractionParentElement)
        ) {
            updateFeedbackStatus = true;
        }
    }

    onDestroy(() => {
        clearTimeout(timeoutId);
    });
</script>

<style>
    .qti-instruction-container {
        margin-block-end: var(--space-3x);

        & :global(.feedback) {
            margin-inline: auto;
            margin-block: 0;

            & :global(p) {
                margin: 1rem;
            }
        }
    }

    :global([data-layouts~='hideFeedbacksLayout']) {
        & .qti-instruction-container :global(.feedback) {
            display: none;
        }
    }
</style>

<svelte:window onfocusin={handleFocusIn} onblur={handleBlurring} />

<div class="qti-instruction-container" {lang} {dir}>
    {#if message}
        <Feedback content={message} {status} fullwidth />
    {/if}
</div>
